import { useEffect, useMemo } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { DragLayer } from '@/dnd/DragLayer';
import { AltTab } from '@/shell/AltTab/AltTab';
import { Bsod } from '@/shell/Bsod/Bsod';
import { ContextMenu } from '@/shell/ContextMenu/ContextMenu';
import { ShutdownOverlay } from '@/shell/Shutdown/ShutdownOverlay';
import { ScreenSaverLayer } from '@/screensaver/ScreenSaverLayer';
import { Taskbar } from '@/shell/Taskbar/Taskbar';
import { useSettingsStore } from '@/settings/store';
import { applyTheme, wallpaperStyle } from '@/theme/applyTheme';
import { useWindowStore } from '@/wm/store';
import { WindowLayer } from '@/wm/WindowLayer';
import { DesktopIcons } from './DesktopIcons';
import css from './Desktop.module.css';

export function Desktop() {
  const wallpaper = useSettingsStore((s) => s.wallpaper);
  const wallpaperPosition = useSettingsStore((s) => s.wallpaperPosition);
  const themeId = useSettingsStore((s) => s.themeId);

  useEffect(() => {
    applyTheme(themeId);
  }, [themeId]);

  const wallpaperCss = useMemo(
    () => wallpaperStyle(wallpaper, wallpaperPosition),
    [wallpaper, wallpaperPosition],
  );

  /**
   * The wallpaper and the icon layer both carry `data-desktop-surface`. A
   * pointerdown landing directly on one of them is a click on bare desktop, and
   * XP deactivated every window when that happened — title bars all go pale.
   */
  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.hasAttribute('data-desktop-surface')) return;
    useWindowStore.getState().blurAll();
  }

  return (
    <div className={css.desktop} onPointerDown={handlePointerDown}>
      <div
        className={css.wallpaper}
        style={wallpaperCss}
        data-desktop-surface=""
        aria-hidden="true"
      />
      <DesktopIcons />
      <WindowLayer />
      <Taskbar />
      {/* One popup for the whole shell. It portals to `document.body`, so its
          place in this tree is about ownership rather than stacking. */}
      <ContextMenu />
      {/* And one drag image, for whatever is being carried — from an icon out
          here, or from inside any folder window. It portals too. */}
      <DragLayer />
      {/* The Alt+Tab box, over the taskbar and the menus both. Renders nothing
          until a chord is held, but its key listeners are always installed. */}
      <AltTab />
      {/* Last, and above everything: Turn Off Computer covers the desktop it
          is switching off, menus and open windows included. */}
      <ShutdownOverlay />
      {/* Above even that, though the two never appear together — choosing Turn
          Off is activity, and activity is what a screen saver waits for. */}
      <ScreenSaverLayer />
      {/* And above the lot: a bugcheck stops the machine, so it outranks
          anything the machine was in the middle of. Renders nothing at all
          until the dice come up — which they mostly never do. */}
      <Bsod />
    </div>
  );
}
